import { FC, useEffect } from 'react';
import {
  Box,
  Dialog,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { setCertStatus } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { userService } from 'src/services/User.service';
import type { Certification } from 'src/types/Certification';
import type { CertStatusActionId } from 'src/types/CertStatus';
import { certKindLabel } from '../utils/certTerminology';
import { getMemberSurvey } from '../Household/householdSurveyUtils';
import { certStatusActionNavigation } from './certStatusActions';
import CertStatusDetail from './CertStatusDetail';

interface CertStatusModalProps {
  open: boolean;
  onClose: () => void;
  cert: Certification | null;
  memberUserId?: number | null;
}

const CertStatusModal: FC<CertStatusModalProps> = ({
  open,
  onClose,
  cert,
  memberUserId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );

  const certStatus = useSelector(
    (state: RootState) => state.newRenterInterface.certStatus ?? {}
  );

  const certId = cert?.id;
  const statusEntry = certId != null ? certStatus[certId] : undefined;

  // Lazy-fetch this cert's status detail when the modal opens, so it's fresh
  // and doesn't depend on the app-load batch fetch having completed. Depend on
  // the specific entry (not the whole map) and don't dispatch on failure —
  // otherwise an empty/failed fetch churns the map reference and re-triggers
  // this effect in an infinite loop.
  useEffect(() => {
    if (!open || certId == null || statusEntry !== undefined) {
      return;
    }
    let cancelled = false;
    userService
      .fetchCertStatus([certId])
      .then((data) => {
        if (!cancelled) {
          dispatch(setCertStatus(data ?? {}));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch cert status', err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, certId, statusEntry, dispatch]);

  if (!cert) {
    return null;
  }

  const detail = certStatus[cert.id] ?? null;
  const selectedCertMemberId = cert.cert_household?.cert_members?.find(
    (m) => m.user_id === memberUserId
  )?.cert_member_id;
  const surveyId =
    selectedCertMemberId != null
      ? getMemberSurvey(cert, selectedCertMemberId)
      : null;

  const handleStatusAction = (id: CertStatusActionId): void => {
    const onBehalfOf =
      memberUserId != null && memberUserId !== user?.id
        ? memberUserId
        : undefined;
    const { path, state: navState } = certStatusActionNavigation(
      id,
      surveyId,
      onBehalfOf
    );
    if (path == null) {
      return;
    }
    onClose();
    if (navState) {
      navigate(path, { state: navState });
    } else {
      navigate(path);
    }
  };

  const content = (
    <Stack gap={2} sx={{ p: 3, pb: 4 }}>
      <CertStatusDetail
        detail={detail}
        selectedUserId={memberUserId}
        selectedCertMemberId={selectedCertMemberId}
        loading={!detail}
        completeStateCertKind={certKindLabel(cert)}
        onAction={handleStatusAction}
      />
    </Stack>
  );

  const header = (
    <Box sx={{ backgroundColor: 'primary.dark', px: 3, pt: 2.5, pb: 2 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: 'common.white', flex: 1 }}
        >
          {certKindLabel(cert, { capitalize: true })} Status
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon sx={{ color: 'common.white', fontSize: 20 }} />
        </IconButton>
      </Stack>
      <Typography
        variant="body2"
        sx={{ color: 'common.white', opacity: 0.75, mt: 0.5 }}
      >
        {cert.category?.name}
        {cert.property?.name ? ` · ${cert.property.name}` : ''}
      </Typography>
    </Box>
  );

  return isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '16px 16px 0 0', maxHeight: '85vh' },
      }}
    >
      {header}
      {content}
    </Drawer>
  ) : (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {header}
      {content}
    </Dialog>
  );
};

export default CertStatusModal;
